import { useState } from "react";
import { toast } from "sonner";
import { Loader2, Plus, X } from "lucide-react";
import { appsApi, errMsg, notifyError } from "@/lib/appsApi";

const field = "rounded-full border border-slate-200 bg-white px-3 py-2 text-sm text-ink outline-none focus:border-sky-600";

export const FileTypesEditor = ({ types, onSaved, onClose }) => {
  const [name, setName] = useState("");
  const [ext, setExt] = useState("");
  const [busy, setBusy] = useState(false);

  const save = async (next, msg) => {
    setBusy(true);
    try {
      const r = await appsApi.put("/admin/file-types", { types: next });
      onSaved(Array.isArray(r?.data?.types) ? r.data.types : next);
      toast.success(msg);
      return true;
    } catch (err) {
      notifyError(errMsg(err, "Saving the file types"));
      return false;
    } finally {
      setBusy(false);
    }
  };

  const add = async (e) => {
    e.preventDefault();
    const n = name.trim();
    const x = ext.trim().toLowerCase().replace(/^\./, "");
    if (!n) return;
    if (types.some((t) => t.name.toLowerCase() === n.toLowerCase())) return notifyError(`"${n}" is already in the list.`);
    const owner = x && types.find((t) => t.ext === x);
    if (owner) return notifyError(`.${x} files are already set to "${owner.name}". Remove that type first, or leave the extension blank.`);
    if (await save([...types, { name: n, ext: x }], `Added "${n}"`)) {
      setName("");
      setExt("");
    }
  };

  const remove = (t) => save(types.filter((x) => x.name !== t.name), `Removed "${t.name}"`);

  return (
    <div className="mt-3 rounded-2xl border border-sky-100 bg-[#F4F9FD] p-4" data-testid="apps-types-editor">
      <div className="flex items-center justify-between">
        <p className="font-mono text-[11px] uppercase tracking-[0.15em] text-slate-500">File types · {types.length}</p>
        <button type="button" onClick={onClose} aria-label="Close" className="rounded-full p-1 text-slate-500 hover:bg-white" data-testid="apps-types-close"><X className="h-4 w-4" /></button>
      </div>
      <ul className="mt-2 flex flex-wrap gap-1.5" data-testid="apps-types-list">
        {types.map((t) => (
          <li key={t.name} className="inline-flex items-center gap-1 rounded-full border border-slate-200 bg-white py-1 pl-3 pr-1 text-xs text-ink" data-testid={`apps-type-chip-${t.name.toLowerCase().replace(/\s+/g, "-")}`}>
            {t.name}{t.ext && <span className="font-mono text-[11px] text-slate-500">.{t.ext}</span>}
            <button type="button" onClick={() => remove(t)} disabled={busy} aria-label={`Remove ${t.name}`} className="rounded-full p-0.5 text-slate-500 hover:bg-red-50 hover:text-red-700" data-testid={`apps-type-remove-${t.name.toLowerCase().replace(/\s+/g, "-")}`}><X className="h-3 w-3" /></button>
          </li>
        ))}
      </ul>
      {/* div, not form: this editor sits inside the upload form */}
      <div className="mt-3 flex flex-wrap items-center gap-2">
        <input value={name} onChange={(e) => setName(e.target.value)} onKeyDown={(e) => e.key === "Enter" && add(e)} maxLength={50} placeholder="New type, e.g. Manual" className={`${field} w-full min-w-0 sm:w-auto sm:flex-1`} data-testid="apps-type-new-name" />
        <input value={ext} onChange={(e) => setExt(e.target.value)} onKeyDown={(e) => e.key === "Enter" && add(e)} maxLength={15} placeholder=".pdf (optional)" className={`${field} min-w-0 flex-1 font-mono sm:w-32 sm:flex-none`} data-testid="apps-type-new-ext" />
        <button type="button" onClick={add} disabled={busy || !name.trim()} className="inline-flex items-center gap-1.5 rounded-full bg-ink px-4 py-2 text-sm font-medium text-white hover:bg-sky-700 disabled:opacity-50" data-testid="apps-type-add">
          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />}Add
        </button>
      </div>
      <p className="mt-2 text-[11px] text-slate-600">With an extension, choosing a file of that kind fills in the type for you. APK details and bundle checks still run for .apk and .zip files whatever they're called.</p>
    </div>
  );
};
